import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const SignUpLayer = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    full_name: ""  // Optional field
  });

  const [agreeTerms, setAgreeTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  
  // Toggle password visibility
  const togglePasswordVisibility = (e) => {
    e.preventDefault();
    setShowPassword(!showPassword);
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    } else if (!/(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter and one number';
    }
    
    // if (!formData.full_name.trim()) {
    //   newErrors.full_name = 'Full name is required';
    // }
    
    if (!agreeTerms) {
      newErrors.agreeTerms = 'You must agree to the terms and conditions';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    console.log('Form submission started');
    
    // Prevent default form submission
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    } else {
      console.warn('Event object is not standard, preventDefault not available');
    }
    
    console.log('Form data:', formData);
    console.log('Agree to terms:', agreeTerms);
    
    // Reset previous errors
    setErrors({});
    
    // Validate the form including terms agreement
    if (!agreeTerms) {
      console.log('Terms not agreed');
      setErrors(prev => ({
        ...prev,
        agreeTerms: 'You must agree to the terms and conditions'
      }));
      // Scroll to the terms checkbox
      const termsCheckbox = document.querySelector('input[type="checkbox"]');
      if (termsCheckbox) {
        termsCheckbox.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    
    // Validate form fields
    const isValid = validateForm();
    console.log('Form validation result:', isValid);
    
    if (!isValid) {
      console.log('Form validation failed');
      // Scroll to the first error
      const firstError = document.querySelector('.border-danger');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Prepare the request data
      const requestData = {
        email: formData.email.trim(),
        password: formData.password,
        full_name: formData.full_name.trim() || formData.username.trim(), // Use full_name if provided, otherwise use username
        username: formData.username.trim()
      };

      console.log('Sending signup request:', requestData);
      
      // Make the API call to sign up the user
      const response = await axios.post(
        `${API_BASE_URL}/auth/signup`,
        requestData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          withCredentials: true // Important for CORS with credentials
        }
      );
      
      console.log('Signup response:', response.data);
      
      if (response.status === 201) {
        // Show success message
        toast.success('Account created successfully! Redirecting to login...');
        
        // Store the access token if available
        if (response.data.access_token) {
          localStorage.setItem('access_token', response.data.access_token);
        }
        
        // Redirect to login after a short delay
        setTimeout(() => {
          navigate("/sign-in");
        }, 2000);
      } else {
        throw new Error('Unexpected response from server');
      }
      
    } catch (error) {
      console.error('Signup error:', error);
      let errorMessage = 'Failed to create account. Please try again.';
      
      if (error.response) {
        // Handle different error statuses
        if (error.response.status === 400) {
          if (error.response.data.detail) {
            errorMessage = error.response.data.detail;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          }
        } else if (error.response.status === 409) {
          errorMessage = 'An account with this email or username already exists.';
        } else if (error.response.status === 422) {
          // Handle validation errors
          const validationErrors = error.response.data.detail;
          if (Array.isArray(validationErrors)) {
            errorMessage = validationErrors.map(err => `${err.loc[1]}: ${err.msg}`).join('\n');
          } else {
            errorMessage = 'Validation error. Please check your input.';
          }
        }
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage = 'Unable to connect to the server. Please check your internet connection.';
      }
      
      toast.error(errorMessage, { autoClose: 5000 });
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <section className='auth bg-base d-flex flex-wrap'>
      <div className='auth-left d-lg-block d-none'>
        <div className='d-flex align-items-center flex-column h-100 justify-content-center'>
          <img src='assets/images/auth/auth-img.png' alt='' />
        </div>
      </div>
      <div className='auth-right py-32 px-24 d-flex flex-column justify-content-center'>
        <div className='max-w-464-px mx-auto w-100'>
          <div>
            <Link to='/' className='mb-40 max-w-290-px'>
              <img src='assets/images/logo.png' alt='' />
            </Link>
            <h4 className='mb-12'>Sign Up to your Account</h4>
            <p className='mb-32 text-secondary-light text-lg'>
              Welcome back! please enter your detail
            </p>
          </div>
          <form onSubmit={handleSubmit} noValidate className="signup-form" id="signup-form">
            <div className='mb-16'>
              <div className='icon-field'>
                <span className='icon top-50 translate-middle-y'>
                  <Icon icon='f7:person' />
                </span>
                <input
                  type='text'
                  name='username'
                  className={`form-control h-56-px bg-neutral-50 radius-12 ${errors.username ? 'border-danger' : ''}`}
                  placeholder='Name'
                  value={formData.username}
                  onChange={handleChange}
                />
              </div>
              {errors.username && (
                <div className='text-danger text-sm mt-1'>{errors.username}</div>
              )}
            </div>
            <div className='mb-16'>
              <div className='icon-field'>
                <span className='icon top-50 translate-middle-y'>
                  <Icon icon='mage:email' />
                </span>
                <input
                  type='email'
                  name='email'
                  className={`form-control h-56-px bg-neutral-50 radius-12 ${errors.email ? 'border-danger' : ''}`}
                  placeholder='Email'
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              {errors.email && (
                <div className='text-danger text-sm mt-1'>{errors.email}</div>
              )}
            </div>
            <div className='mb-20'>
                <div className='position-relative'>
                <div className='icon-field'>
                  <span className='icon top-50 translate-middle-y'>
                    <Icon icon='solar:lock-password-outline' />
                  </span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name='password'
                    className={`form-control h-56-px bg-neutral-50 radius-12 ${errors.password ? 'border-danger' : ''}`}
                    id='your-password'
                    placeholder='Password'
                    value={formData.password}
                    onChange={handleChange}
                    autoComplete='new-password'
                  />
                </div>
                <button
                  type='button'
                  className='toggle-password border-0 bg-transparent p-0 position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light'
                  onClick={togglePasswordVisibility}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon icon={showPassword ? 'ri:eye-off-line' : 'ri:eye-line'} />
                </button>
              </div>
              <span className={`mt-12 text-sm ${errors.password ? 'text-danger' : 'text-secondary-light'}`}>
                {errors.password || 'Your password must have at least 8 characters, one uppercase letter, and one number'}
              </span>
            </div>
            <div className=''>
              <div className='d-flex justify-content-between gap-2'>
                <div className='form-check style-check d-flex align-items-start mb-3'>
                  <input
                    className={`form-check-input border ${errors.agreeTerms ? 'border-danger' : 'border-neutral-300'} mt-4`}
                    type='checkbox'
                    checked={agreeTerms}
                    onChange={(e) => {
                      setAgreeTerms(e.target.checked);
                      if (errors.agreeTerms) {
                        setErrors(prev => ({
                          ...prev,
                          agreeTerms: ''
                        }));
                      }
                    }}
                    id='condition'
                  />
                  <label
                    className={`form-check-label text-sm ${errors.agreeTerms ? 'text-danger' : ''}`}
                    htmlFor='condition'
                  >
                    By creating an account means you agree to the{" "}
                    <Link to='#' className='text-primary-600 fw-semibold'>
                      Terms &amp; Conditions
                    </Link>{" "}
                    and our{" "}
                    <Link to='#' className='text-primary-600 fw-semibold'>
                      Privacy Policy
                    </Link>
                  </label>
                </div>
                {errors.agreeTerms && (
                  <div className='text-danger text-sm mt-1'>{errors.agreeTerms}</div>
                )}
              </div>
            </div>
            <button
              type='submit'
              className='btn btn-primary w-100 h-56-px radius-12 fw-600 mb-24 d-flex align-items-center justify-content-center gap-2'
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span>
                  Creating Account...
                </>
              ) : (
                'Sign Up'
              )}
            </button>
            <div className='mt-32 center-border-horizontal text-center'>
              <span className='bg-base z-1 px-4'>Or sign up with</span>
            </div>
            <div className='mt-32 d-flex align-items-center gap-3'>
              <button
                type='button'
                className='fw-semibold text-primary-light py-16 px-24 w-50 border radius-12 text-md d-flex align-items-center justify-content-center gap-12 line-height-1 bg-hover-primary-50'
              >
                <Icon
                  icon='ic:baseline-facebook'
                  className='text-primary-600 text-xl line-height-1'
                />
                Facebook
              </button>
              <button
                type='button'
                className='fw-semibold text-primary-light py-16 px-24 w-50 border radius-12 text-md d-flex align-items-center justify-content-center gap-12 line-height-1 bg-hover-primary-50'
              >
                <Icon
                  icon='logos:google-icon'
                  className='text-primary-600 text-xl line-height-1'
                />
                Google
              </button>
            </div>
            <div className='mt-32 text-center text-sm'>
              <p className='mb-0'>
                Already have an account?{" "}
                <Link to='/sign-in' className='text-primary-600 fw-semibold'>
                  Sign In
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default SignUpLayer;
